import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link2, Link2Off, Wand2, RefreshCw } from "lucide-react";

const SCORE_TONE = (s) =>
    s >= 0.9 ? "bg-emerald-100 text-emerald-800"
        : s >= 0.75 ? "bg-blue-100 text-blue-800"
            : "bg-amber-100 text-amber-800";

/**
 * Payment ↔ Statement reconciliation section. Shows unlinked payments with
 * top-3 statement match candidates and lets the user link or auto-link.
 */
export default function PaymentReconcileSection() {
    const [overview, setOverview] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [o, c] = await Promise.all([
                api.get("/payments/reconcile/overview"),
                api.get("/payments/reconcile/candidates?limit=50"),
            ]);
            setOverview(o.data);
            setItems(c.data.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const link = async (paymentId, statementId) => {
        setBusy(true);
        try {
            await api.post(`/payments/${paymentId}/link-statement`, { statement_id: statementId });
            toast.success("Payment linked to statement");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const autoLink = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/payments/reconcile/auto-link");
            toast.success(`Auto-linked ${data.linked} payment(s) with ≥90% confidence`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!overview) return null;

    return (
        <div className="rounded-lg border border-stone-200 bg-white overflow-hidden mb-6" data-testid="payment-reconcile">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 flex-wrap gap-2">
                <div>
                    <div className="eyebrow flex items-center gap-1.5">
                        <Link2 size={11} /> Reconciliation
                    </div>
                    <div className="text-sm text-stone-700 mt-1">
                        <strong className="text-stone-900">{overview.linked_to_statement}</strong> of {overview.total_payments} payments linked ·{" "}
                        <span className={overview.unlinked > 0 ? "text-amber-700" : "text-emerald-700"}>
                            {overview.unlinked} unlinked
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="reconcile-refresh">
                        <RefreshCw size={12} className="mr-1.5" /> Refresh
                    </Button>
                    {overview.unlinked > 0 && (
                        <Button size="sm" onClick={autoLink} disabled={busy}
                                className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="reconcile-auto-link">
                            <Wand2 size={12} className="mr-1.5" /> Auto-link high-confidence
                        </Button>
                    )}
                </div>
            </div>

            {items.length === 0 ? (
                <div className="p-6 text-center text-sm text-stone-500">
                    All payments are linked to statements. Nothing to reconcile.
                </div>
            ) : (
                <div className="divide-y divide-stone-100">
                    {items.slice(0, 20).map((it) => (
                        <div key={it.payment_id} className="px-4 py-3" data-testid={`recon-row-${it.payment_id}`}>
                            <div className="flex items-baseline justify-between flex-wrap gap-2">
                                <div className="text-sm">
                                    <span className="font-medium tabular-nums">{fmtMoney(it.amount_paid)}</span>
                                    <span className="text-stone-500"> · {fmtDate(it.payment_date)} · {it.statement_month || "—"}</span>
                                </div>
                                <span className="text-[10px] uppercase tracking-widest text-stone-500">
                                    {it.posted ? "Posted" : "Not posted"}
                                </span>
                            </div>
                            {it.candidates.length === 0 ? (
                                <div className="mt-2 text-[12px] text-stone-500">
                                    No candidate statement — upload the matching statement first.
                                </div>
                            ) : (
                                <div className="mt-2 space-y-1.5">
                                    {it.candidates.map((c) => (
                                        <div key={c.statement_id} className="flex items-center justify-between gap-2 rounded border border-stone-100 px-2.5 py-1.5 hover:border-stone-300 transition-colors">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${SCORE_TONE(c.score)}`}>
                                                    {Math.round(c.score * 100)}%
                                                </span>
                                                <div className="text-[12px] text-stone-700 truncate">
                                                    Stmt {c.statement_month} · due {fmtDate(c.due_date)} · {fmtMoney(c.total_amount_due)}
                                                    {c.variance !== 0 && (
                                                        <span className={`ml-1 ${c.variance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                                                            (Δ {c.variance > 0 ? "+" : ""}{fmtMoney(Math.abs(c.variance))})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button size="sm" variant="outline" onClick={() => link(it.payment_id, c.statement_id)}
                                                    disabled={busy} data-testid={`recon-link-${it.payment_id}-${c.statement_id}`}>
                                                <Link2Off size={11} className="mr-1" /> Link
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    {items.length > 20 && (
                        <div className="px-4 py-2 text-[11px] text-stone-500">
                            Showing first 20 of {items.length} unlinked payments.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
